import React, { useState, useEffect } from "react";
import { Alert } from "react-bootstrap";
import { AdminUser, DeviceBase } from "@preveil-api";
import {
  Account, OrgUserStatus, GetUserDevicesApiResponse, useGetUserDevicesMutation, Message, SettingsErrorMessages, useAppDispatch, isSameUser, usePostUsersOrgsByEntityIdAllowAddDeviceMutation, AdminErrorMessages,
  AdminInfoMessages, MessageAnchors
} from "src/common";
import { InfoPopover, AlertWithSwitch, ToolbarWrapper } from "src/components";
import { ListView } from ".";
import { uiActions } from "src/store";

type AllProps = {
  current_user: AdminUser;
  current_account: Account;
  allow_add_devices?: boolean;
  add_device_overrides: string[];
};

function DevicePanelComponent(props: AllProps) {
  const { current_user, allow_add_devices, add_device_overrides, current_account } = props;
  const { devices } = current_user;
  const dispatch = useAppDispatch();
  const [user_devices, setUserDevices] = useState<DeviceBase[]>(devices || []);
  const [loading_devices, setLoadingDevices] = useState<boolean>(false);
  const [add_device_allowed_user, setAddDeviceAllowedUser] = useState<boolean>(false);
  const [postUsersOrgsByEntityIdAllowAddDevice] = usePostUsersOrgsByEntityIdAllowAddDeviceMutation();
  const [getDevices] = useGetUserDevicesMutation();

  useEffect(() => {
    const allowed = allow_add_devices || !!add_device_overrides.find(_user_id => isSameUser(_user_id, current_user.user_id));
    setAddDeviceAllowedUser(allowed);
  }, [allow_add_devices, add_device_overrides, current_user]);

  // Description: Fetches the devices for the user when the admin clicks on a users row.
  useEffect(() => {
    getUserDevices();
  }, [current_user]);

  async function getUserDevices() {
    // Preveil/subsume_accounts return 403 when on invited status and trying to fetch devices.
    // Non Preveil/external accounts return an empty array.
    const is_invited_status = current_user.status === OrgUserStatus.invited;
    if (!!current_account && !!current_user.user_id && !is_invited_status) {
      setLoadingDevices(true);
      await getDevices({ user_id: current_account.user_id, for_user: current_user.user_id })
        .unwrap()
        .then((response: GetUserDevicesApiResponse) => {
          setUserDevices(response);
          setLoadingDevices(false);
        })
        .catch((msg: string) => {
          dispatch(uiActions.handleRequestErrors(new Message(SettingsErrorMessages.error_fetching_devices), msg));
          setLoadingDevices(false);
        });
    } else if (is_invited_status) {
      // If the user is in invited status, we set the devices to an empty array.
      // Otherwise it could keep the devices from the last user selected.
      setUserDevices([]);
    }
  }

  // Description: Overrides the add device setting for this user.
  async function toggleAddDeviceOverride() {
    if (!!current_account.org_info) {
      const { org_info } = current_account;
      const is_allowed = !add_device_allowed_user;
      await postUsersOrgsByEntityIdAllowAddDevice({
        account_ids: Account.getAccountIdentifiers(current_account),
        body: {
          entityId: org_info.org_id,
          is_allowed,
          userId: current_user.user_id
        }
      })
        .unwrap()
        .then(() => {
          setAddDeviceAllowedUser(!add_device_allowed_user);
        })
        .catch((error: any) => {
          dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_overriding_add_device_setting), error));
        });
    }
  };

  function deviceInfoMessage(): string {
    return add_device_allowed_user ? AdminInfoMessages.disable_add_device : AdminInfoMessages.enable_add_device;
  }

  return <>
    {allow_add_devices ? (
      <Alert variant="info" className="admin-alert admin-switch mw-100">
        <div className="switch-preveil-container">
          <p>Adding devices is allowed for all users. You can disable adding devices from Settings.</p>
          <InfoPopover message={deviceInfoMessage()} />
        </div>
      </Alert>
    ) :
      <AlertWithSwitch
        popover={true}
        toggleState={add_device_allowed_user}
        handleChange={toggleAddDeviceOverride}
        popover_message={deviceInfoMessage()}
      >
        <span>{AdminInfoMessages.add_device_alert.replace(MessageAnchors.message_content, `${add_device_allowed_user ? "allowed to add" : "blocked from adding"}`)}</span>
      </AlertWithSwitch>
    }
    <ToolbarWrapper
      refreshAction={getUserDevices}
      refreshTooltipContent="Refresh Devices"
      isLoading={loading_devices}
      paginationInfo={<span>{user_devices.length} Device{user_devices.length > 1 ? "s" : ""}</span>}
    />
    <ListView devices={user_devices} org_user={current_user} refresh={getUserDevices} loading={loading_devices} current_account={current_account} />
  </>;
}

export default React.memo(DevicePanelComponent);
